// src/pages/BlogPostDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { 
  FaHome, 
  FaChevronRight, 
  FaCalendarAlt, 
  FaClock, 
  FaUser,
  FaShareAlt,
  FaFacebookF,
  FaTwitter,
  FaLinkedinIn,
  FaWhatsapp,
  FaEnvelope,
  FaBookmark,
  FaPrint,
  FaEye,
  FaTags,
  FaArrowLeft,
  FaArrowRight,
  FaCheck
} from 'react-icons/fa';
import Section from '../components/common/Section';
import Heading from '../components/common/Heading';
import Button from '../components/common/Button';
import Card from '../components/common/Card';

const BlogPostDetailPage = () => {
  const { blogId } = useParams();
  const [blogPost, setBlogPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isShareMenuOpen, setIsShareMenuOpen] = useState(false);
  const navigate = useNavigate();
  
  // In a real application, this would be an API call
  // For demo purposes, we're using mock data
  useEffect(() => {
    // Simulating API call
    setLoading(true);
    
    // Mock blog data - in production this would come from an API
    const mockBlogs = [
      {
        id: "1",
        title: "The Future of Digital Skills in Sierra Leone",
        publishDate: "2025-03-20",
        updateDate: "2025-03-25",
        readTime: "8 min read",
        author: {
          name: "Ing. Alfred Mbayoh",
          role: "CEO, Leotech SL",
          image: "/images/team/alfred_mbayoh.jpg"
        },
        category: "career",
        coverImage: "/images/resources/blog-digital-skills.jpg",
        excerpt: "Exploring how digital skills are transforming employment prospects for youth in Sierra Leone and what skills will be most in demand in the coming years.",
        content: `
          <p>Digital skills have become increasingly important in today's global economy, and Sierra Leone is no exception. As the country continues to develop its technological infrastructure and connectivity improves, the demand for digitally skilled professionals is growing rapidly. This article explores the current state of digital skills in Sierra Leone and the future outlook for youth seeking to enter the workforce with these valuable capabilities.</p>
          
          <h2>The Current Landscape</h2>
          
          <p>In Sierra Leone, the digital skills gap is significant. Traditional education systems have been slow to adapt to the rapidly evolving digital landscape, leaving many graduates unprepared for the demands of the modern workplace. However, this gap also presents an opportunity for youth who are willing to invest in developing their digital competencies.</p>
          
          <p>According to recent surveys, employers in Sierra Leone increasingly value candidates with the following digital skills:</p>
          
          <ul>
            <li>Computer-Aided Design (CAD) for engineering and construction</li>
            <li>Building Information Modeling (BIM) for architecture and construction management</li>
            <li>Geographic Information Systems (GIS) for mapping and land management</li>
            <li>Programming and web development for software and application creation</li>
            <li>Digital marketing and social media management for business growth</li>
            <li>Data analysis and visualization for informed decision-making</li>
          </ul>
          
          <p>These skills are not just valuable for employment with established companies; they also enable entrepreneurship and freelance opportunities in the growing digital economy.</p>
          
          <h2>Future Trends and Opportunities</h2>
          
          <p>Looking ahead to the next five years, several digital skill areas are projected to see significant growth in demand within Sierra Leone:</p>
          
          <h3>1. Mobile App Development</h3>
          
          <p>With mobile phone penetration continuing to increase across Sierra Leone, the demand for locally relevant mobile applications is expected to grow substantially. Youth with skills in app development for Android (the dominant platform in the country) will find numerous opportunities to create solutions for local challenges.</p>
          
          <h3>2. Digital Construction Technologies</h3>
          
          <p>As Sierra Leone continues to invest in infrastructure development, professionals with BIM and advanced CAD skills will be highly sought after. These technologies allow for more efficient and accurate design, planning, and execution of construction projects.</p>
          
          <h3>3. Drone Technology and Mapping</h3>
          
          <p>Drones are revolutionizing land surveying, environmental monitoring, and agricultural assessment. Skills in drone operation, photogrammetry, and geospatial data analysis will be valuable across multiple sectors, from mining to agriculture.</p>
          
          <h3>4. Data Analysis and Visualization</h3>
          
          <p>As organizations in Sierra Leone become more data-driven, the ability to collect, analyze, and visualize data will become increasingly important. These skills can be applied across industries, from healthcare to business intelligence.</p>
          
          <h2>Preparing for the Future</h2>
          
          <p>For youth in Sierra Leone looking to position themselves for success in the digital economy, a proactive approach to skills development is essential. Traditional university education may provide a foundation, but it should be supplemented with specialized training in specific digital tools and technologies.</p>
          
          <p>Here are some strategies for developing marketable digital skills:</p>
          
          <ol>
            <li><strong>Targeted Training Programs:</strong> Seek out specialized training programs like those offered by Leotech that focus on specific, in-demand digital skills.</li>
            <li><strong>Project-Based Learning:</strong> Apply skills to real-world projects to build a portfolio that demonstrates your capabilities to potential employers or clients.</li>
            <li><strong>Continuous Learning:</strong> Digital technologies evolve rapidly, so commit to ongoing learning to keep your skills current and relevant.</li>
            <li><strong>Professional Networking:</strong> Connect with others in your field to learn about opportunities and stay informed about industry trends.</li>
            <li><strong>Entrepreneurial Mindset:</strong> Look for ways to apply digital skills to solve local problems, potentially creating your own business opportunities.</li>
          </ol>
          
          <h2>Conclusion</h2>
          
          <p>The future of digital skills in Sierra Leone is promising, with growing demand across multiple sectors of the economy. Youth who invest in developing these skills now will be well-positioned to take advantage of emerging opportunities, whether through traditional employment, freelancing, or entrepreneurship.</p>
          
          <p>At Leotech, we remain committed to equipping Sierra Leonean youth with the digital skills they need to succeed in this evolving landscape. Through our training programs, we aim to bridge the digital skills gap and contribute to a more technologically capable workforce that can drive sustainable economic development.</p>
          
          <p>The journey toward a digitally skilled workforce in Sierra Leone has just begun, and the future looks bright for those willing to embrace the opportunities that digital technologies present.</p>
        `,
        tags: ["digital skills", "employment", "youth", "technology", "career development", "Sierra Leone"],
        views: 432,
        relatedPosts: [2, 3, 4]
      },
      {
        id: "2",
        title: "Building Information Modeling: Revolutionizing Construction in West Africa",
        publishDate: "2025-02-15",
        updateDate: null,
        readTime: "6 min read",
        author: {
          name: "Mohamed Sylvanus Kamara",
          role: "Civil Engineer & Leotech Tutor",
          image: "/images/team/mohamed_kamara.jpg"
        },
        category: "technical",
        coverImage: "/images/resources/blog-bim.jpg",
        excerpt: "How BIM technology is changing the face of construction projects in West Africa, improving efficiency, reducing costs, and enhancing collaboration.",
        content: `
          <p>Building Information Modeling (BIM) is transforming construction practices around the world, and West Africa is beginning to experience this revolution. This powerful approach to digital construction design and management offers significant benefits for a region with rapidly growing infrastructure needs. In this article, we explore how BIM is being adopted in West Africa and the impact it's having on the construction industry.</p>
          
          <h2>What is BIM?</h2>
          
          <p>Building Information Modeling is more than just 3D modeling software; it's a collaborative process that allows architects, engineers, contractors, and owners to create and manage information about a construction project across its entire lifecycle. BIM creates a detailed digital representation of the physical and functional characteristics of a building or infrastructure project.</p>
          
          <p>Key components of BIM include:</p>
          
          <ul>
            <li>3D visualization of building components</li>
            <li>Information embedding within the model (specifications, costs, schedules)</li>
            <li>Simulation capabilities (structural analysis, energy performance)</li>
            <li>Coordination tools to detect clashes between different systems</li>
            <li>Documentation generation for construction and facility management</li>
          </ul>
          
          <h2>The Current State of BIM in West Africa</h2>
          
          <p>Adoption of BIM in West Africa is still in its early stages compared to Europe, North America, and parts of Asia, but it is gaining momentum. In Sierra Leone and neighboring countries, several factors are driving increased interest in BIM:</p>
          
          <ol>
            <li><strong>International Projects:</strong> Foreign companies and donors bringing BIM requirements to major infrastructure projects</li>
            <li><strong>Efficiency Needs:</strong> Growing recognition of BIM's potential to reduce waste and improve project outcomes</li>
            <li><strong>Skills Development:</strong> Increasing availability of BIM training through organizations like Leotech</li>
            <li><strong>Technology Access:</strong> Improving internet connectivity and computer hardware availability</li>
          </ol>
          
          <h2>Benefits for West African Construction</h2>
          
          <p>The adoption of BIM in West Africa offers numerous benefits that are particularly relevant to the region's construction challenges:</p>
          
          <h3>1. Cost Reduction and Efficiency</h3>
          
          <p>In a region where construction resources can be limited, BIM's ability to optimize material use and reduce rework is invaluable. Early clash detection alone can save significant costs that would otherwise be spent fixing issues discovered during construction.</p>
          
          <h3>2. Improved Quality Control</h3>
          
          <p>BIM enables better quality assurance through detailed modeling and simulation before physical construction begins. This is especially important in areas where construction quality has historically been inconsistent.</p>
          
          <h3>3. Enhanced Collaboration</h3>
          
          <p>BIM facilitates better communication among project stakeholders, which is crucial in environments where coordination between different disciplines and contractors has traditionally been challenging.</p>
          
          <h3>4. Better Infrastructure Planning</h3>
          
          <p>For rapidly developing urban areas in West Africa, BIM offers powerful tools for infrastructure planning and visualization, allowing better integration of new projects into existing environments.</p>
          
          <h2>Challenges to Widespread Adoption</h2>
          
          <p>Despite its benefits, several challenges slow BIM adoption in West Africa:</p>
          
          <ul>
            <li><strong>Skills Gap:</strong> Limited number of professionals trained in BIM technologies</li>
            <li><strong>Initial Costs:</strong> Software licenses and hardware requirements present financial barriers</li>
            <li><strong>Regulatory Framework:</strong> Lack of BIM standards and requirements in local building codes</li>
            <li><strong>Internet Connectivity:</strong> Unreliable internet access in some areas limits cloud-based collaboration</li>
          </ul>
          
          <h2>The Path Forward</h2>
          
          <p>To accelerate BIM adoption in West Africa, several strategies are being implemented:</p>
          
          <h3>1. Education and Training</h3>
          
          <p>Organizations like Leotech are playing a crucial role in building BIM capacity through specialized training programs. By equipping young professionals with BIM skills, these initiatives are creating a workforce ready to implement digital construction methods.</p>
          
          <h3>2. Industry Partnerships</h3>
          
          <p>Collaborations between local firms and international companies with BIM expertise are helping to transfer knowledge and establish best practices adapted to the West African context.</p>
          
          <h3>3. Government Initiatives</h3>
          
          <p>Some governments in the region are beginning to recognize the potential of BIM and are exploring policies to encourage its adoption in public projects.</p>
          
          <h2>Conclusion</h2>
          
          <p>Building Information Modeling represents a significant opportunity for the construction industry in West Africa to improve efficiency, quality, and sustainability. While challenges to adoption remain, the growing interest and investment in BIM skills and technology suggest a bright future for digital construction in the region.</p>
          
          <p>As more projects demonstrate the tangible benefits of BIM, and as more professionals develop expertise in these technologies, we can expect to see an acceleration in adoption across Sierra Leone and neighboring countries. This digital transformation has the potential to significantly enhance the quality and efficiency of construction projects, contributing to better infrastructure development throughout West Africa.</p>
        `,
        tags: ["BIM", "construction", "technology", "engineering", "West Africa", "infrastructure"],
        views: 285,
        relatedPosts: [1, 4, 7]
      },
      {
        id: "3",
        title: "From Trainee to Professional: Success Stories from Leotech Alumni",
        publishDate: "2025-01-28",
        updateDate: null,
        readTime: "10 min read",
        author: {
          name: "Bailor Bah",
          role: "Director of Programs, Leotech SL",
          image: "/images/team/bailor_bah.jpg"
        },
        category: "success-stories",
        coverImage: "/images/resources/blog-success.jpg",
        excerpt: "Highlighting the journeys of several Leotech graduates who have successfully transitioned into professional careers using their digital skills.",
        content: `
          <p>At Leotech, our mission is to empower youth with marketable digital skills that transform their career prospects. Nothing illustrates the impact of our work better than the success stories of our graduates. In this article, we highlight the journeys of several Leotech alumni who have leveraged their training to build thriving careers.</p>
          
          <h2>Aminata Ngaujah: Revolutionizing Land Surveying</h2>
          
          <p>When Aminata joined Leotech's Cohort 8, she was already working as a junior surveyor at the Ministry of Lands while studying Building and Civil Engineering at Milton Margai Technical University. Despite her academic background, she found herself frustrated by traditional manual drawing methods that were time-consuming and prone to errors.</p>
          
          <p>"I was looking for a way to improve my efficiency and accuracy," Aminata recalls. "When I heard about Leotech's AutoCAD and Global Mapper training, I immediately recognized the opportunity to modernize my approach to surveying."</p>
          
          <p>Through Leotech's program, Aminata mastered AutoCAD for 2D plotting and 3D design, as well as Global Mapper for geospatial analysis. She also gained foundational skills in drone technology for aerial surveying.</p>
          
          <p>Today, Aminata stands out as the youngest engineer in her department who can leverage these digital tools for accurate and efficient engineering design. "These digital skills have completely transformed how I work," she says. "Tasks that used to take days can now be completed in hours, with greater precision and detail."</p>
          
          <h2>Lansana Farroh: From Employee to Entrepreneur</h2>
          
          <p>Lansana's journey illustrates how digital skills can open doors to entrepreneurship. As a civil engineering graduate from Fourah Bay College, Lansana had a solid theoretical foundation but lacked the practical software skills that employers were seeking.</p>
          
          <p>"I was struggling to find meaningful employment that used my engineering training," Lansana explains. "I kept seeing job postings requiring BIM and CAD skills that weren't covered in my university curriculum."</p>
          
          <p>After completing Leotech's training in Chief Architect software, Lansana's career trajectory changed dramatically. He began by securing a position with a local construction firm but soon discovered opportunities for freelance work.</p>
          
          <p>"Learning Chief Architect gave me the ability to create professional 3D models and detailed floor plans that architects and contractors value highly," he says. "Before long, I was earning more from my freelance projects than from my day job."</p>
          
          <p>Lansana made the leap to full-time freelancing in 2023 and now runs a successful design consultancy, working with clients throughout Freetown and beyond. His expertise in Chief Architect has made him a sought-after specialist in the region.</p>
          
          <h2>Monica Musa: From Trainee to Trainer</h2>
          
          <p>Monica's story demonstrates how digital skills acquisition can come full circle. She first came to Leotech as a student seeking to supplement her engineering education with practical computer skills.</p>
          
          <p>"The conventional education system focuses heavily on theory, but I knew I needed hands-on digital skills to be competitive," Monica says. "Leotech's Computer-Aided Design program gave me that practical edge."</p>
          
          <p>Monica excelled in the program, mastering multiple design software platforms including AutoCAD, Revit, and Chief Architect. Her aptitude caught the attention of Leotech's leadership team, who invited her to join as a tutor after her graduation.</p>
          
          <p>"Teaching these skills to other young people has been incredibly rewarding," Monica reflects. "I understand exactly what our students are going through because I was in their position not long ago."</p>
          
          <p>As an IT professional and Leotech tutor, Monica now plays a crucial role in spreading digital literacy to the next generation of engineers and designers in Sierra Leone.</p>
          
          <h2>Key Factors in Their Success</h2>
          
          <p>While each of these success stories is unique, several common factors have contributed to the positive outcomes:</p>
          
          <h3>1. Practical, Hands-On Training</h3>
          
          <p>All of our successful graduates emphasize the importance of Leotech's practical approach to teaching digital skills. By focusing on real-world applications rather than theory alone, our programs prepare students to apply their knowledge immediately in professional contexts.</p>
          
          <h3>2. Industry-Relevant Skills</h3>
          
          <p>Leotech constantly monitors industry trends to ensure our training programs focus on the most in-demand skills. This market-oriented approach ensures that graduates possess capabilities that employers and clients actually need.</p>
          
          <h3>3. Peer Learning Environment</h3>
          
          <p>Many graduates highlight the benefits of Leotech's peer-to-peer learning model, where students learn not just from instructors but from collaborative problem-solving with fellow trainees.</p>
          
          <h3>4. Ongoing Support Network</h3>
          
          <p>Leotech's community doesn't end when training does. Our alumni network provides ongoing support, job leads, and opportunities for continued learning and growth.</p>
          
          <h2>Impact Beyond Individual Success</h2>
          
          <p>The success of Leotech graduates extends beyond their individual career advancement. As these skilled professionals apply their knowledge in various sectors, they contribute to the broader digital transformation of Sierra Leone's economy.</p>
          
          <p>Aminata's work is helping modernize land management practices in government. Lansana's architectural visualizations are raising the standard for construction design in the private sector. Monica's teaching is multiplying the impact by training even more skilled professionals.</p>
          
          <h2>Conclusion</h2>
          
          <p>These success stories illustrate the transformative power of digital skills training. By bridging the gap between traditional education and industry needs, Leotech is helping create pathways to professional success for Sierra Leonean youth.</p>
          
          <p>As we continue to expand our programs and reach more students, we look forward to adding many more success stories to these inspiring examples. The journey from trainee to professional is challenging, but with the right skills and support, it's a path that's increasingly accessible to motivated young people in Sierra Leone.</p>
        `,
        tags: ["success stories", "alumni", "career growth", "professional development", "digital skills", "training"],
        views: 514,
        relatedPosts: [1, 4, 5]
      },
      {
        id: "4",
        title: "Drone Technology for Land Surveying: A Practical Guide",
        publishDate: "2024-12-10",
        updateDate: "2025-01-05",
        readTime: "12 min read",
        author: {
          name: "Mohamed Fornah",
          role: "Geologist & Drone Specialist",
          image: "/images/team/mohamed_fornah.jpg"
        },
        category: "technical",
        coverImage: "/images/resources/blog-drone.jpg",
        excerpt: "An introduction to using drone technology for land surveying applications, including equipment selection, regulations, and basic techniques.",
        content: `
          <p>Drone technology has revolutionized land surveying, offering unprecedented efficiency, accuracy, and cost-effectiveness compared to traditional methods. In Sierra Leone and across West Africa, this technology has particular relevance for addressing challenges in land management, urban planning, and resource mapping. This guide provides a practical introduction to drone surveying for professionals looking to adopt this powerful technology.</p>
          
          <h2>Why Use Drones for Surveying?</h2>
          
          <p>Before diving into the how-to aspects, it's worth understanding the significant advantages drones offer for surveying applications:</p>
          
          <ul>
            <li><strong>Efficiency:</strong> Survey large areas in a fraction of the time required by traditional methods</li>
            <li><strong>Access:</strong> Safely capture data from difficult or dangerous terrain</li>
            <li><strong>Cost-effectiveness:</strong> Reduce labor costs and equipment needs compared to ground-based methods</li>
            <li><strong>Data richness:</strong> Collect high-resolution imagery and precise elevation data simultaneously</li>
            <li><strong>Versatility:</strong> Apply to numerous use cases from construction monitoring to environmental assessment</li>
          </ul>
          
          <p>In the West African context, these benefits are particularly valuable given the challenges of difficult terrain, limited infrastructure, and resource constraints.</p>
          
          <h2>Selecting the Right Equipment</h2>
          
          <p>Choosing appropriate drone equipment is crucial for successful surveying work. Here are the key considerations:</p>
          
          <h3>Drone Platform Types</h3>
          
          <p>For surveying applications, two main types of drones are commonly used:</p>
          
          <ol>
            <li><strong>Multirotor drones:</strong> These offer excellent stability, precise control for detailed mapping of smaller areas (typically under 50 hectares), and the ability to take off and land vertically. The DJI Phantom 4 RTK and DJI Mavic 3 Enterprise are popular examples that offer good balance of cost and capability for entry-level surveying.</li>
            
            <li><strong>Fixed-wing drones:</strong> These aircraft-like drones are more efficient for covering large areas and offer longer flight times, making them suitable for large-scale surveying projects. Examples include the senseFly eBee X and WingtraOne, though they generally come with higher price tags and require more space for takeoff and landing.</li>
          </ol>
          
          <p>For most beginners and small-scale operations in West Africa, a multirotor drone is recommended due to their lower cost, ease of use, and flexibility.</p>
          
          <h3>Essential Components for Surveying Drones</h3>
          
          <p>Beyond the basic drone platform, several components are particularly important for surveying applications:</p>
          
          <ul>
            <li><strong>High-quality camera:</strong> Minimum 12MP resolution with a global shutter is preferable for mapping applications</li>
            <li><strong>RTK/PPK capability:</strong> Real-Time Kinematic or Post-Processed Kinematic GPS systems dramatically improve position accuracy from meters to centimeters</li>
            <li><strong>Sufficient battery capacity:</strong> Multiple batteries are essential for covering larger areas</li>
            <li><strong>Reliable controller:</strong> Preferably with a bright screen visible in sunlight</li>
            <li><strong>Rugged transportation case:</strong> Critical for protecting equipment in field conditions typical in West Africa</li>
          </ul>
          
          <h2>Regulatory Considerations in West Africa</h2>
          
          <p>Drone regulations vary significantly across West African countries and are still evolving in many jurisdictions. In Sierra Leone, drone operations currently require:</p>
          
          <ul>
            <li>Registration of the drone with the Civil Aviation Authority</li>
            <li>Operator certification or licensing</li>
            <li>Flight permissions for specific missions, particularly in urban areas or near airports</li>
            <li>Adherence to altitude restrictions (typically limited to 400 feet above ground level)</li>
            <li>Maintenance of visual line of sight with the aircraft during operations</li>
          </ul>
          
          <p>It's essential to research and comply with the specific regulations in your operating area, as unauthorized drone use can result in equipment confiscation, fines, or legal consequences.</p>
          
          <h2>Field Operation Basics</h2>
          
          <p>Successful drone surveying requires careful planning and execution. Here's a basic workflow:</p>
          
          <h3>1. Mission Planning</h3>
          
          <p>Before heading to the field:</p>
          
          <ul>
            <li>Define the survey area boundaries and required resolution</li>
            <li>Plan flight parameters (altitude, overlap, speed)</li>
            <li>Check for obstacles and restricted airspace</li>
            <li>Prepare ground control points (GCPs) strategy if using</li>
            <li>Monitor weather forecasts (avoid rain, fog, or high winds)</li>
          </ul>
          
          <p>Software like DJI GS Pro, Pix4Dcapture, or MapPilot can help automate mission planning for optimal coverage and data quality.</p>
          
          <h3>2. Ground Control Points (GCPs)</h3>
          
          <p>For high-accuracy surveys, establishing GCPs is crucial:</p>
          
          <ul>
            <li>Place markers that will be clearly visible in aerial imagery</li>
            <li>Distribute GCPs evenly throughout the survey area</li>
            <li>Record precise GPS coordinates of each GCP using RTK GNSS equipment</li>
            <li>Use at least 5-10 GCPs for a typical project area</li>
          </ul>
          
          <h3>3. Field Operation</h3>
          
          <p>When executing the survey:</p>
          
          <ul>
            <li>Conduct a thorough pre-flight check of all equipment</li>
            <li>Set up a safe takeoff/landing area away from obstacles</li>
            <li>Monitor battery levels continuously</li>
            <li>Maintain visual line of sight with the drone</li>
            <li>Watch for changing weather conditions</li>
            <li>Have a backup plan for emergency situations</li>
          </ul>
          
          <h3>4. Data Capture</h3>
          
          <p>For optimal results:</p>
          
          <ul>
            <li>Ensure 70-80% overlap between images both forward and side</li>
            <li>Fly at a consistent altitude throughout the mission</li>
            <li>Maintain steady flight speed</li>
            <li>Capture nadir (straight down) images for basic mapping</li>
            <li>Consider adding oblique images for 3D modeling applications</li>
          </ul>
          
          <h2>Data Processing Workflow</h2>
          
          <p>After collection, drone data must be processed to create usable surveying outputs:</p>
          
          <h3>1. Data Preparation</h3>
          
          <ul>
            <li>Download and organize all imagery</li>
            <li>Review images for quality and coverage</li>
            <li>Prepare GCP coordinates in the required format</li>
          </ul>
          
          <h3>2. Photogrammetry Processing</h3>
          
          <p>Using specialized software like Pix4D, Agisoft Metashape, or DroneDeploy:</p>
          
          <ul>
            <li>Import and align images</li>
            <li>Incorporate GCP data</li>
            <li>Generate dense point cloud</li>
            <li>Create digital surface model (DSM)</li>
            <li>Produce orthomosaic (seamless aerial image)</li>
          </ul>
          
          <h3>3. Deliverable Creation</h3>
          
          <p>Common outputs include:</p>
          
          <ul>
            <li>Orthomosaic maps</li>
            <li>Digital elevation models</li>
            <li>Contour maps</li>
            <li>3D models</li>
            <li>Volumetric calculations</li>
            <li>CAD-compatible files for engineering design</li>
          </ul>
          
          <h2>Challenges and Solutions in the West African Context</h2>
          
          <p>Drone surveying in West Africa presents unique challenges:</p>
          
          <h3>1. Internet Connectivity</h3>
          
          <p><strong>Challenge:</strong> Limited internet access for downloading maps, uploading data, or RTK corrections.</p>
          <p><strong>Solution:</strong> Prepare offline base maps before field work, use PPK instead of RTK where appropriate, and plan for data processing at locations with reliable connectivity.</p>
          
          <h3>2. Power Availability</h3>
          
          <p><strong>Challenge:</strong> Unreliable access to electricity for charging batteries and running computers.</p>
          <p><strong>Solution:</strong> Invest in portable power banks, solar chargers, and multiple battery sets. Consider laptops with extended battery life for field processing.</p>
          
          <h3>3. Environmental Conditions</h3>
          
          <p><strong>Challenge:</strong> Heat, dust, humidity, and seasonal heavy rainfall can affect equipment.</p>
          <p><strong>Solution:</strong> Use protective cases, schedule flights during optimal weather windows, and maintain rigorous equipment maintenance routines.</p>
          
          <h2>Conclusion</h2>
          
          <p>Drone technology offers transformative potential for land surveying in West Africa, enabling faster, safer, and more comprehensive data collection than traditional methods. While getting started requires investment in equipment and skills development, the efficiency gains and new capabilities make it increasingly essential for modern surveying operations.</p>
          
          <p>At Leotech, we provide specialized training in drone operation and data processing for surveying applications, helping professionals in Sierra Leone and beyond adopt this powerful technology. With proper training, equipment, and methodology, drone surveying can significantly enhance land management, urban planning, and infrastructure development throughout the region.</p>
        `,
        tags: ["drones", "surveying", "technology", "land management", "GIS", "photogrammetry"],
        views: 376,
        relatedPosts: [2, 3, 5]
      }
    ];
    
    console.log("blogId", blogId);
    const foundBlog = mockBlogs.find(b => b.id === blogId);
    
    setTimeout(() => {
      if (foundBlog) {
        setBlogPost(foundBlog);
        
        // Get related posts
        if (foundBlog.relatedPosts && foundBlog.relatedPosts.length > 0) {
          const related = mockBlogs.filter(blog => 
            foundBlog.relatedPosts.includes(parseInt(blog.id)) && blog.id !== blogId
          );
          setRelatedPosts(related);
        }
        
        setLoading(false);
      } else {
        setError('Blog post not found');
        setLoading(false);
      }
    }, 500); // Simulate loading delay
  }, [blogId]);
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  
  // Share blog post
  const shareBlogPost = (platform) => {
    const url = window.location.href;
    const title = blogPost.title;
    
    let shareUrl;
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(title + ' ' + url)}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent('I thought you might find this article interesting: ' + url)}`;
        break;
      default:
        shareUrl = null;
    }
    
    if (shareUrl) {
      window.open(shareUrl, '_blank');
    }
    
    // Close share menu
    setIsShareMenuOpen(false);
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <Section background="white" padding="lg" className="pt-32">
        <div className="text-center">
          <Heading level={2}>Blog Post Not Found</Heading>
          <p className="text-gray-600 mb-6">We couldn't find the blog post you're looking for.</p>
          <Button to="/resources" variant="primary">View All Resources</Button>
        </div>
      </Section>
    );
  }
  
  return (
    <>
      <Helmet>
        <title>{blogPost.title} - Leotech Blog</title>
        <meta name="description" content={blogPost.excerpt} />
      </Helmet>
      
      {/* Hero Banner */}
      <section 
        className="relative pt-32 pb-16 overflow-hidden"
        style={{
          backgroundImage: `url(${blogPost.coverImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Dark overlay for better text visibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-80"></div>
        
        {/* Content */}
        <div className="container mx-auto px-4 relative z-10">
          {/* Breadcrumbs */}
          <div className="flex items-center mb-8 text-white text-sm">
            <Link to="/" className="flex items-center hover:text-secondary transition-colors">
              <FaHome className="mr-2" />
              Home
            </Link>
            <FaChevronRight className="mx-2 text-xs text-gray-300" />
            <Link to="/resources" className="hover:text-secondary transition-colors">Resources</Link>
            <FaChevronRight className="mx-2 text-xs text-gray-300" />
            <Link to="/resources?tab=blogs" className="hover:text-secondary transition-colors">Blog</Link>
            <FaChevronRight className="mx-2 text-xs text-gray-300" />
            <span className="text-secondary font-medium">Article</span>
          </div>
          
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block bg-secondary text-white text-sm font-medium rounded-full px-3 py-1 mb-4">
                {(() => {
                  switch (blogPost.category) {
                    case 'technical':
                      return 'Technical Guide';
                    case 'career':
                      return 'Career Development';
                    case 'success-stories':
                      return 'Success Story';
                    case 'events':
                      return 'Event';
                    default:
                      return blogPost.category.charAt(0).toUpperCase() + blogPost.category.slice(1);
                  }
                })()}
              </span>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                {blogPost.title}
              </h1>
              
              <div className="flex flex-col md:flex-row justify-center items-center gap-4 text-white mb-8">
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full overflow-hidden mr-3">
                    <img
                      src={blogPost.author.image}
                      alt={blogPost.author.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://placehold.co/100x100/1a237e/ffffff?text=${blogPost.author.name.split(' ').map(n => n[0]).join('')}`;
                      }}
                    />
                  </div>
                  <div>
                    <span className="font-medium">{blogPost.author.name}</span>
                    <div className="text-sm text-gray-300">{blogPost.author.role}</div>
                  </div>
                </div>
                
                <div className="flex items-center text-sm md:ml-6">
                  <div className="flex items-center mr-4">
                    <FaCalendarAlt className="mr-2" />
                    <span>{formatDate(blogPost.publishDate)}</span>
                  </div>
                  <div className="flex items-center mr-4">
                    <FaClock className="mr-2" />
                    <span>{blogPost.readTime}</span>
                  </div>
                  <div className="flex items-center">
                    <FaEye className="mr-2" />
                    <span>{blogPost.views} views</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Main Article Content */}
      <Section background="white" padding="lg">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-7 gap-8">
            {/* Article Content - Main Column */}
            <div className="lg:col-span-5">
              <Card className="mb-8">
                {/* Article Content */}
                <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: blogPost.content }} />
                
                {/* Update Notice */}
                {blogPost.updateDate && (
                  <div className="mt-8 text-sm text-gray-500 italic border-t border-gray-200 pt-4">
                    Last updated on {formatDate(blogPost.updateDate)}
                  </div>
                )}
                
                {/* Tags */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex flex-wrap gap-2 items-center">
                    <FaTags className="text-gray-400 text-xs" />
                    {blogPost.tags.map((tag, index) => (
                      <span 
                        key={index}
                        className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                
                {/* Author Bio */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                    <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={blogPost.author.image}
                        alt={blogPost.author.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `https://placehold.co/300x300/1a237e/ffffff?text=${blogPost.author.name.split(' ').map(n => n[0]).join('')}`;
                        }}
                      />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-primary-dark mb-1">{blogPost.author.name}</h3>
                      <p className="text-secondary font-medium mb-3">{blogPost.author.role}</p>
                      <p className="text-gray-600">
                        At Leotech, we empower Sierra Leonean youth with digital skills that transform careers. 
                        Our instructors bring real-world expertise to provide practical, hands-on training that 
                        bridges the gap between traditional education and industry needs.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Navigation Buttons */}
                <div className="mt-8 pt-6 border-t border-gray-200 flex flex-wrap justify-between gap-4">
                  <Button 
                    variant="outline-primary" 
                    onClick={() => navigate(-1)}
                    icon={<FaArrowLeft />}
                    iconPosition="left"
                  >
                    Back
                  </Button>
                  
                  <div className="flex space-x-3">
                    <div className="relative">
                      <Button 
                        variant="outline-primary" 
                        onClick={() => setIsShareMenuOpen(!isShareMenuOpen)}
                        icon={<FaShareAlt />}
                      >
                        Share
                      </Button>
                      
                      {/* Share Menu Popup */}
                      {isShareMenuOpen && (
                        <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 p-1 z-50">
                          <button
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                            onClick={() => shareBlogPost('facebook')}
                          >
                            <FaFacebookF className="mr-3 text-blue-600" /> Facebook
                          </button>
                          <button
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                            onClick={() => shareBlogPost('twitter')}
                          >
                            <FaTwitter className="mr-3 text-blue-400" /> Twitter
                          </button>
                          <button
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                            onClick={() => shareBlogPost('linkedin')}
                          >
                            <FaLinkedinIn className="mr-3 text-blue-700" /> LinkedIn
                          </button>
                          <button
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                            onClick={() => shareBlogPost('whatsapp')}
                          >
                            <FaWhatsapp className="mr-3 text-green-500" /> WhatsApp
                          </button>
                          <button
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                            onClick={() => shareBlogPost('email')}
                          >
                            <FaEnvelope className="mr-3 text-gray-500" /> Email
                          </button>
                        </div>
                      )}
                    </div>
                    
                    <Button 
                      variant="outline-primary" 
                      onClick={() => window.print()}
                      icon={<FaPrint />}
                    >
                      Print
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
            
            {/* Sidebar */}
            <div className="lg:col-span-2">
              <div className="sticky top-32 space-y-6">
                {/* Table of Contents - automatically generated from h2 headings */}
                <Card className="mb-6" shadow="md">
                  <Heading level={4} className="mb-4">In This Article</Heading>
                  <ul className="space-y-2 text-sm">
                    {blogPost.content.match(/<h2>(.*?)<\/h2>/g)?.map((match, index) => {
                      const title = match.replace(/<h2>|<\/h2>/g, '');
                      return (
                        <li key={index} className="border-l-2 border-gray-200 hover:border-primary pl-3 py-1">
                          <a 
                            href={`#${title.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '')}`}
                            className="text-gray-700 hover:text-primary"
                          >
                            {title}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </Card>
                
                {/* Related Articles */}
                {relatedPosts.length > 0 && (
                  // BlogPostDetailPage.js (continued)
                  <Card shadow="md">
                    <Heading level={4} className="mb-4">Related Articles</Heading>
                    <div className="space-y-4">
                      {relatedPosts.map((post) => (
                        <div key={post.id} className="group">
                          <Link to={`/blog/${post.id}`} className="flex flex-col space-y-2">
                            <div className="h-32 overflow-hidden rounded-md">
                              <img 
                                src={post.coverImage}
                                alt={post.title}
                                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = `https://placehold.co/300x200/1a237e/ffffff?text=${post.title.replace(/ /g, '+')}`;
                                }}
                              />
                            </div>
                            <h5 className="font-bold text-primary-dark group-hover:text-primary transition-colors line-clamp-2">
                              {post.title}
                            </h5>
                            <div className="text-xs text-gray-500 flex justify-between">
                              <span>{formatDate(post.publishDate)}</span>
                              <span>{post.readTime}</span>
                            </div>
                          </Link>
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <Link 
                        to="/resources?tab=blogs" 
                        className="inline-flex items-center text-primary font-medium hover:text-primary-dark"
                      >
                        View All Blog Posts <FaArrowRight className="ml-2" />
                      </Link>
                    </div>
                  </Card>
                )}
                
                {/* Quick Actions */}
                <Card shadow="md">
                  <Heading level={4} className="mb-4">Quick Actions</Heading>
                  <div className="space-y-3">
                    <Button 
                      variant="outline-primary" 
                      className="w-full justify-between"
                      icon={<FaBookmark />}
                      onClick={() => alert('Bookmark feature will be available soon!')}
                    >
                      Bookmark Article
                    </Button>
                    
                    <Button 
                      variant="outline-primary" 
                      className="w-full justify-between"
                      icon={<FaShareAlt />}
                      onClick={() => setIsShareMenuOpen(!isShareMenuOpen)}
                    >
                      Share Article
                    </Button>
                    
                    <Button 
                      variant="outline-primary" 
                      className="w-full justify-between"
                      icon={<FaPrint />}
                      onClick={() => window.print()}
                    >
                      Print Article
                    </Button>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </Section>
      
      {/* More Articles Section */}
      <Section background="gray" padding="lg">
        <div className="text-center mb-12">
          <Heading level={2} align="center" className="mb-4" withAccent>
            More Articles You Might Like
          </Heading>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Continue exploring our resources to enhance your digital skills
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {relatedPosts.map((post) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100 transform transition-all duration-300 hover:-translate-y-2 hover:shadow-lg"
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={post.coverImage}
                  alt={post.title}
                  className="w-full h-full object-cover transition-transform duration-500 transform hover:scale-110"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `https://placehold.co/800x400/1a237e/ffffff?text=${post.title.replace(/ /g, '+')}`;
                  }}
                />
                <div className="absolute top-0 left-0 bg-primary-dark text-white px-3 py-1 m-3 rounded-md font-medium text-sm">
                  Blog Post
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="text-xl font-bold text-primary-dark mb-3 line-clamp-2">{post.title}</h3>
                
                <p className="text-gray-600 mb-4 line-clamp-3">
                  {post.excerpt}
                </p>
                
                <div className="flex justify-between items-center text-sm text-gray-500 mb-4">
                  <div className="flex items-center">
                    <FaCalendarAlt className="mr-1" />
                    <span>{formatDate(post.publishDate)}</span>
                  </div>
                  <div className="flex items-center">
                    <FaClock className="mr-1" />
                    <span>{post.readTime}</span>
                  </div>
                </div>
                
                <Link
                  to={`/blog/${post.id}`}
                  className="inline-flex items-center text-primary font-medium hover:text-primary-dark"
                >
                  Read Article <FaArrowRight className="ml-2" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
        
        <div className="text-center mt-10">
          <Button 
            variant="primary" 
            to="/resources?tab=blogs" 
            icon={<FaArrowRight />}
            iconPosition="right"
          >
            Browse All Blog Posts
          </Button>
        </div>
      </Section>
      
      {/* Newsletter Section */}
      <Section background="white" padding="lg">
        <div className="max-w-4xl mx-auto">
          <Card className="p-8 md:p-12 border-t-4 border-secondary">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <Heading level={2} className="mb-4">
                  Stay Updated
                </Heading>
                <p className="text-gray-600 mb-6">
                  Subscribe to our newsletter to receive regular updates about new articles, 
                  resources, and upcoming events. Be the first to know when we publish new content.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-start">
                    <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                      <FaCheck className="text-green-600 text-xs" />
                    </div>
                    <span className="text-gray-700">Weekly article roundups</span>
                  </li>
                  <li className="flex items-start">
                    <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                      <FaCheck className="text-green-600 text-xs" />
                    </div>
                    <span className="text-gray-700">Exclusive content for subscribers</span>
                  </li>
                  <li className="flex items-start">
                    <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                      <FaCheck className="text-green-600 text-xs" />
                    </div>
                    <span className="text-gray-700">Early access to new programs</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="font-bold text-primary-dark text-xl mb-4">Join Our Newsletter</h3>
                <form>
                  <div className="mb-4">
                    <label htmlFor="newsletter-name" className="block mb-2 text-sm font-medium text-gray-700">
                      Your Name
                    </label>
                    <input
                      type="text"
                      id="newsletter-name"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Enter your name"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="newsletter-email" className="block mb-2 text-sm font-medium text-gray-700">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="newsletter-email"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Enter your email"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <div className="flex items-center">
                      <input
                        id="newsletter-terms"
                        type="checkbox"
                        className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <label htmlFor="newsletter-terms" className="ml-2 text-sm text-gray-700">
                        I agree to receive email newsletters from Leotech
                      </label>
                    </div>
                  </div>
                  
                  <Button 
                    variant="secondary" 
                    type="submit"
                    className="w-full"
                  >
                    Subscribe
                  </Button>
                </form>
              </div>
            </div>
          </Card>
        </div>
      </Section>
    </>
  );
};

export default BlogPostDetailPage;